import { useCallback, useState } from 'react';

import { useWebSocket } from 'src/hooks/useWebSocket';
import { LotteryActiveBatch } from 'src/types/Lottery';

export const LOTTERY_TRAY_WS_URL = 'lottery/tray/';

interface TrayEventData {
  batches?: LotteryActiveBatch[];
  batch?: LotteryActiveBatch;
  batch_id?: number;
}

export interface UseLotteryTraySocketResult {
  batches: LotteryActiveBatch[];
  isConnected: boolean;
  connectionError: string | null;
  reconnect: () => void;
}

/**
 * The floating tray's live feed. The server pushes a snapshot on connect and
 * add/update/remove events after; there is no polling and no timer. A drop is
 * reconciled by useWebSocket reconnecting, which replays the snapshot.
 */
export function useLotteryTraySocket(
  enabled = true
): UseLotteryTraySocketResult {
  const [batches, setBatches] = useState<LotteryActiveBatch[]>([]);

  const upsert = useCallback((batch: LotteryActiveBatch): void => {
    setBatches((prev) => {
      const index = prev.findIndex((item) => item.id === batch.id);
      if (index === -1) {
        return [...prev, batch];
      }
      const next = prev.slice();
      next[index] = batch;
      return next;
    });
  }, []);

  const genericHandler = useCallback(
    (type: string, data: unknown): void => {
      const payload = (data ?? {}) as TrayEventData;
      switch (type) {
        case 'tray.snapshot':
          setBatches(payload.batches ?? []);
          break;
        case 'tray.batch_created':
        case 'tray.batch_updated':
          if (payload.batch) {
            upsert(payload.batch);
          }
          break;
        case 'tray.batch_removed':
          if (typeof payload.batch_id === 'number') {
            setBatches((prev) =>
              prev.filter((item) => item.id !== payload.batch_id)
            );
          }
          break;
        default:
          break;
      }
    },
    [upsert]
  );

  const { isConnected, connectionError, reconnect } = useWebSocket({
    enabled,
    url: LOTTERY_TRAY_WS_URL,
    genericHandler,
    reconnectAttempts: 20,
    reconnectDelay: 3000,
  });

  return { batches, isConnected, connectionError, reconnect };
}

export default useLotteryTraySocket;
